import { useMemo, useState } from "react";
import localeText from "../../../../locale";
import { formatPrice } from "../../../../utilities/formatPrice";
import { localeISO } from "../../../../utilities/localeIso";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import useGetDashboard from "../../../../hooks/useGetDashboard";
import useAdminPendingCounts from "./hooks/useAdminPendingCounts";
import PeriodFilter, { PERIODS, buildDashboardFilter } from "./components/PeriodFilter";
import AttentionPanel from "./components/AttentionPanel";
import StatTile from "./components/StatTile";
import UsersByTypeChart from "./components/UsersByTypeChart";
import ProductsStatusChart from "./components/ProductsStatusChart";
import "./AdminHome.css";

// Sem dados (erro na API) mostra "—" em vez de um zero enganoso
const formatEuro = (value) => (value == null ? "—" : `EUR ${formatPrice(value)}`);
const formatNumber = (value) => (value == null ? "—" : value.toLocaleString(localeISO));

const AdminHome = () => {
  const { showErrorToast } = useToastContext();
  const [period, setPeriod] = useState(PERIODS.ALL);
  const [range, setRange] = useState(null);

  const filter = useMemo(() => buildDashboardFilter(period, range), [period, range]);
  const { dashboardData, isLoading, hasError } = useGetDashboard({
    onError: (code) => showErrorToast({ code }),
    filter,
  });
  const pendingCounts = useAdminPendingCounts();

  const data = Array.isArray(dashboardData) ? null : dashboardData;
  const loading = isLoading || (!data && !hasError);

  const statGroups = [
    {
      key: "revenue",
      title: localeText("adminHomeGroupRevenue"),
      stats: [
        { label: localeText("salesRevenue"), icon: "pi pi-dollar", value: formatEuro(data?.amountRevenueSales) },
        { label: localeText("feeRevenue"), icon: "pi pi-percentage", value: formatEuro(data?.amountPlatformTaxesRetained) },
        { label: localeText("revenueFromUserRegistration"), icon: "pi pi-user-plus", value: formatEuro(data?.amountRevenueRegister) },
        { label: localeText("totalofCommissionsPaid"), icon: "pi pi-share-alt", value: formatEuro(data?.amountComissions) },
      ],
    },
    {
      key: "operation",
      title: localeText("adminHomeGroupOperation"),
      stats: [
        { label: localeText("completedOrders"), icon: "pi pi-check-square", value: formatNumber(data?.amountFinishedOrders) },
        { label: localeText("registeredStores"), icon: "pi pi-shopping-bag", value: formatNumber(data?.amountStoresRegistered) },
        { label: localeText("totalOfUsersPublishedAds"), icon: "pi pi-box", value: formatNumber(data?.totalUserPublished) },
      ],
    },
    {
      key: "withdraws",
      title: localeText("adminHomeGroupWithdraws"),
      stats: [
        { label: localeText("quantityOfWithdrawsCompleted"), icon: "pi pi-check-circle", value: formatNumber(data?.totalWithdrawCompleted) },
        { label: localeText("totalAmountWithdrawsCompleted"), icon: "pi pi-wallet", value: formatEuro(data?.amountWithdrawCompleted) },
      ],
    },
  ];

  return (
    <div className="admin-home">
      <AttentionPanel counts={pendingCounts} />

      <div className="admin-home__toolbar">
        <h2 className="admin-home__section-title">{localeText("adminHomeIndicators")}</h2>
        <PeriodFilter
          period={period}
          range={range}
          onChange={(nextPeriod, nextRange) => {
            setPeriod(nextPeriod);
            setRange(nextRange);
          }}
        />
      </div>

      {statGroups.map((group) => (
        <section key={group.key} className="admin-home__group">
          <h3 className="admin-home__group-title">{group.title}</h3>
          <div className="admin-home__stats">
            {group.stats.map((stat) => (
              <StatTile key={stat.label} {...stat} loading={loading} />
            ))}
          </div>
        </section>
      ))}

      <div className="admin-home__charts">
        <UsersByTypeChart data={data} loading={loading} />
        <ProductsStatusChart data={data} loading={loading} />
      </div>
    </div>
  );
};

export default AdminHome;
